import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ShoppingCart, AlertTriangle, Package, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8" data-testid="loading-dashboard">Carregando...</div>;
  }

  const statCards = [
    {
      title: 'Pedidos em Produção',
      value: stats?.pedidos_em_producao || 0,
      icon: ShoppingCart,
      color: 'from-blue-500 to-cyan-400',
      testId: 'stat-pedidos-producao'
    },
    {
      title: 'Próximos ao Prazo',
      value: stats?.pedidos_proximos_prazo || 0,
      icon: AlertTriangle,
      color: 'from-orange-500 to-amber-400',
      testId: 'stat-proximos-prazo'
    },
    {
      title: 'Estoque Baixo',
      value: stats?.ingredientes_baixo_estoque || 0,
      icon: Package,
      color: 'from-red-500 to-pink-400',
      testId: 'stat-estoque-baixo'
    },
    {
      title: 'Receita Mensal',
      value: `R$ ${(stats?.receita_mensal || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'from-green-500 to-emerald-400',
      testId: 'stat-receita-mensal'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-container">
      <div>
        <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent mb-2">
          Dashboard
        </h1>
        <p className="text-gray-600">Bem-vindo, {user?.nome}!</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.title} className="border-pink-100 bg-white/80 backdrop-blur-sm shadow-md hover:shadow-xl transition-shadow" data-testid={card.testId}>
              <CardHeader className="p-3 pb-2 sm:p-6 sm:pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardDescription className="min-w-0 text-[10px] leading-tight uppercase tracking-wide font-semibold sm:text-xs">
                    {card.title}
                  </CardDescription>
                  <div className={`shrink-0 rounded-xl bg-gradient-to-br p-1.5 sm:p-2 ${card.color}`}>
                    <Icon className="h-4 w-4 text-white sm:h-5 sm:w-5" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <p className="break-words text-xl font-bold leading-tight tracking-tight text-gray-800 sm:text-3xl" data-testid={`${card.testId}-value`}>{card.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-pink-100 bg-white/80 backdrop-blur-sm shadow-md">
        <CardHeader>
          <CardTitle className="text-2xl">Visão Geral</CardTitle>
          <CardDescription>Resumo da sua operação</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {stats?.ingredientes_baixo_estoque > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="alert-estoque-baixo">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <p className="text-sm font-medium text-red-800">
                  Você tem {stats.ingredientes_baixo_estoque} ingrediente(s) com estoque baixo. Verifique a seção de ingredientes.
                </p>
              </div>
            </div>
          )}
          {stats?.pedidos_proximos_prazo > 0 && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="alert-proximos-prazo">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <p className="text-sm font-medium text-orange-800">
                  Você tem {stats.pedidos_proximos_prazo} pedido(s) próximos ao prazo de entrega.
                </p>
              </div>
            </div>
          )}
          {stats?.ingredientes_baixo_estoque === 0 && stats?.pedidos_proximos_prazo === 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="all-good">
              <p className="text-sm font-medium text-green-800">Tudo certo! Nenhum alerta no momento.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
